import { useState } from 'react';
import { Mail, Send, Loader2, CheckCircle, Sparkles } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { cn } from '../../lib/utils';

export function Newsletter() {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!email.trim()) {
      setError('Please enter your email');
      return;
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError('Please enter a valid email');
      return;
    }

    setIsSubmitting(true);
    // Simulate API call
    await new Promise((resolve) => setTimeout(resolve, 1500));
    setIsSuccess(true);
    setIsSubmitting(false);

    setTimeout(() => {
      setIsSuccess(false);
      setEmail('');
    }, 3000);
  };

  return (
    <section className="py-20 bg-gradient-to-r from-[#004E64] via-[#006B87] to-[#004E64]" data-testid="newsletter">
      <div className="max-w-4xl mx-auto px-4">
        <div className="relative">
          {/* Decorative Elements */}
          <div className="absolute -top-8 left-10 w-16 h-16 bg-[#FF6B35]/20 rounded-full blur-2xl" />
          <div className="absolute -bottom-8 right-10 w-24 h-24 bg-[#2A9D8F]/20 rounded-full blur-2xl" />

          {/* Content */}
          <div className="relative text-center">
            <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm rounded-full px-4 py-2 mb-6">
              <Sparkles className="w-4 h-4 text-[#E9C46A]" />
              <span className="text-white/80 text-sm">Get Exclusive Deals</span>
            </div>

            <h2 
              className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4"
              style={{ fontFamily: 'Playfair Display, serif' }}
            >
              Subscribe to Our Newsletter
            </h2>
            <p className="text-white/70 text-lg mb-8 max-w-2xl mx-auto">
              Be the first to know about our latest offers, travel tips, and exclusive discounts. 
              No spam, just travel inspiration!
            </p>

            {/* Form */}
            <form onSubmit={handleSubmit} className="max-w-xl mx-auto">
              <div className="flex flex-col sm:flex-row gap-4">
                <div className="flex-1 relative">
                  <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <Input
                    type="email"
                    placeholder="Enter your email address"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className={cn(
                      'pl-12 pr-4 py-6 rounded-full bg-white text-[#1A1A1A]',
                      'placeholder:text-gray-400 focus:ring-2 focus:ring-[#FF6B35]',
                      error && 'ring-2 ring-red-400'
                    )}
                    disabled={isSubmitting || isSuccess}
                  />
                </div>
                <Button
                  type="submit"
                  disabled={isSubmitting || isSuccess}
                  className={cn(
                    'px-8 py-6 rounded-full font-semibold transition-all',
                    isSuccess 
                      ? 'bg-green-500 hover:bg-green-600' 
                      : 'bg-[#FF6B35] hover:bg-[#E85A2D]'
                  )}
                >
                  {isSubmitting ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : isSuccess ? (
                    <><CheckCircle className="w-5 h-5 mr-2" /> Subscribed!</>
                  ) : (
                    <><Send className="w-5 h-5 mr-2" /> Subscribe</>
                  )}
                </Button>
              </div>
              {error && <p className="text-red-300 text-sm mt-2">{error}</p>}
            </form>

            {/* Trust Badges */}
            <div className="mt-8 flex flex-wrap items-center justify-center gap-6 text-white/60 text-sm">
              <span className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4" /> No Spam
              </span>
              <span className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4" /> Unsubscribe Anytime
              </span>
              <span className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4" /> 10K+ Subscribers
              </span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
